import { useState, useEffect, useRef } from 'react';
import { exchangeAPI } from '../lib/api';
import { FiX, FiLoader, FiDownload, FiPaperclip, FiMail, FiExternalLink, FiClock, FiUser } from 'react-icons/fi';

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRecipients(recipients) {
  if (!recipients || recipients.length === 0) return '';
  return recipients.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join('; ');
}

export default function EmailDetailModal({ mailboxId, messageId, subject, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    loadMessage();
  }, [mailboxId, messageId]);

  const loadMessage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await exchangeAPI.getMessageDetails(mailboxId, messageId);
      setMessage(response.data.message);
    } catch (err) {
      console.error('Failed to load message:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Write HTML body into iframe for safe rendering
  useEffect(() => {
    if (message?.bodyHtml && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              padding: 16px;
              margin: 0;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            img { max-width: 100%; height: auto; }
            a { color: #7c3aed; }
            table { max-width: 100%; border-collapse: collapse; }
            td, th { padding: 4px 8px; }
            pre { white-space: pre-wrap; overflow-x: auto; }
          </style>
        </head>
        <body>${message.bodyHtml}</body>
        </html>
      `);
      doc.close();

      // Auto-resize iframe to content height
      const resizeIframe = () => {
        if (iframeRef.current?.contentDocument?.body) {
          const height = iframeRef.current.contentDocument.body.scrollHeight;
          iframeRef.current.style.height = Math.min(height + 32, 600) + 'px';
        }
      };
      iframeRef.current.onload = resizeIframe;
      setTimeout(resizeIframe, 200);
    }
  }, [message?.bodyHtml]);

  const handleDownloadEml = async () => {
    try {
      setDownloading(true);
      // Pass subject to backend — avoids an extra Graph round-trip for filename
      const knownSubject = message?.subject || subject;
      const response = await exchangeAPI.downloadMessage(mailboxId, messageId, knownSubject);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = (knownSubject || 'email').replace(/[<>:"/\\|?*]/g, '_').substring(0, 100) + '.eml';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
    } catch (err) {
      console.error('Failed to download email:', err);
      setError('Failed to download email file');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      setDownloadingAttachment(attachment.id);
      const response = await exchangeAPI.downloadAttachment(mailboxId, messageId, attachment.id);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.name);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      setError('Failed to download attachment');
    } finally {
      setDownloadingAttachment(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <FiMail className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {loading ? 'Loading...' : (message?.subject || subject || 'Email Details')}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Download as .eml */}
            <button
              onClick={handleDownloadEml}
              disabled={loading || downloading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              title="Download as .eml file (opens in Outlook)"
            >
              {downloading ? (
                <FiLoader className="w-4 h-4 animate-spin" />
              ) : (
                <FiDownload className="w-4 h-4" />
              )}
              Download .eml
            </button>
            {/* Open in Outlook */}
            {message?.webLink && (
              <button
                onClick={() => window.open(message.webLink, '_blank')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title="Open in Outlook Web"
              >
                <FiExternalLink className="w-4 h-4" />
                Outlook
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <FiLoader className="w-8 h-8 animate-spin text-purple-600" />
              <span className="ml-3 text-gray-600">Loading email...</span>
            </div>
          ) : error ? (
            <div className="m-6 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              {error}
            </div>
          ) : message ? (
            <div>
              {/* Email Metadata */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 space-y-2">
                {/* From */}
                {message.from && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">From:</span>
                    <span className="text-gray-900">
                      {message.from.name && <span className="font-medium">{message.from.name}</span>}
                      {message.from.email && <span className="text-gray-500"> &lt;{message.from.email}&gt;</span>}
                    </span>
                  </div>
                )}

                {/* To */}
                {message.toRecipients?.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">To:</span>
                    <span className="text-gray-900">{formatRecipients(message.toRecipients)}</span>
                  </div>
                )}

                {/* CC */}
                {message.ccRecipients?.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">CC:</span>
                    <span className="text-gray-900">{formatRecipients(message.ccRecipients)}</span>
                  </div>
                )}

                {/* BCC */}
                {message.bccRecipients?.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">BCC:</span>
                    <span className="text-gray-900">{formatRecipients(message.bccRecipients)}</span>
                  </div>
                )}

                {/* Date */}
                <div className="flex items-start gap-2 text-sm">
                  <span className="font-medium text-gray-600 w-16 flex-shrink-0">Date:</span>
                  <span className="text-gray-900 flex items-center gap-1">
                    <FiClock className="w-3.5 h-3.5 text-gray-400" />
                    {new Date(message.receivedDateTime || message.sentDateTime).toLocaleString()}
                  </span>
                </div>

                {/* Importance */}
                {message.importance && message.importance !== 'normal' && (
                  <div className="flex items-start gap-2 text-sm">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">Priority:</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      message.importance === 'high' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {message.importance.charAt(0).toUpperCase() + message.importance.slice(1)}
                    </span>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {message.attachments?.length > 0 && (
                <div className="px-6 py-3 bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FiPaperclip className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">
                      {message.attachments.length} Attachment{message.attachments.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {message.attachments.map((att) => (
                      <button
                        key={att.id}
                        onClick={() => handleDownloadAttachment(att)}
                        disabled={downloadingAttachment === att.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-sm text-gray-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                        title={`Download ${att.name} (${formatFileSize(att.size)})`}
                      >
                        {downloadingAttachment === att.id ? (
                          <FiLoader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <FiDownload className="w-3.5 h-3.5 text-amber-600" />
                        )}
                        <span className="truncate max-w-[200px]">{att.name}</span>
                        {att.size && (
                          <span className="text-xs text-gray-400">({formatFileSize(att.size)})</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Body */}
              <div className="px-6 py-4">
                {message.bodyHtml ? (
                  <iframe
                    ref={iframeRef}
                    title="Email content"
                    className="w-full border-0 min-h-[200px]"
                    referrerPolicy="no-referrer"
                    style={{ height: '400px' }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                    {message.bodyText || message.bodyPreview || 'No content available'}
                  </pre>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
